import React, {
  Fragment,
  useState,
  useEffect,
  createContext,
  useContext,
  useMemo,
  useRef,
} from "react";
import { Grid } from "@mui/material";
import { HorizontalSeparator } from "../styled/SidePanel.style";
import RadioSelectGroup from "../RadioSelectGroup";
import SidePanelCard from "./StyledSidePanelCard";
import SitesDetailsTile from "./SitesDetailTile";
import RangeSelectSlider from "./RangeSelectSlider";
import { ToggleTitleWrapper, ToggleSwitch } from "../styled/ToggleTile.styled";
import { WebMapContext } from "../../pages/sections/web-map/WebMapGoogleMap";
import {
  DemandScoreTypes,
  SupplyType,
  DemandScoreLabels,
  Modules,
  Names_For_Filter_OPs
} from "../../constants/WebMap";
import { getFilters } from "../../services/api/filterService";
import WebMapConnectionType from "./WebMapConnectionTypes";
import WebMapDataType from "./WebMapDataTypes";
import CapacitySidePanel from "./CapacitySidePanel";
import EdotCoSitesCards from "./EdotCoSitesCards";
import OperatorSitesCards from "./OperatorSitesCards";
import MarketSharePanel from "./web-map/market-share/MarketSharePanel";
import IBSPanel from "./web-map/ibs/IBSPanel";
import UploadFilesSidePanel from "./UploadFilesSidePanel";
import PanelTools from "./PanelTools";
import { AffluenceFilterComponent } from "./web-map/demand";
import { CommonTypeTabsGroup } from "./common";
import { fetchMinMaxSupplyOp } from "../../services/api/mapGeohashes.api";

export const Context = createContext({ value: null, setValue: () => { } });
export default function SidePanelCards({
  show,
  openedTabs,
  setOpenedTabs,
  isEyeIconVisibleAsGreenForSites,
  selectedCountryPrems
}) {
  const [isSupplyChecked, setIsSupplyChecked] = useState(false);
  const [isDemandChecked, setIsDemandChecked] = useState(false);
  const [value, setValue] = useState(SupplyType.RSRP);
  const [isSupplyVisible, setIsSupplyVisible] = useState(false);
  const [isDemandVisible, setIsDemandVisible] = useState(false);
  const [isSitesVisible, setIsSitesVisible] = useState(false); // site eye icon
  const [isSitesChecked, setIsSitesChecked] = useState(false); // site drop down
  const [isECoSitesChecked, setIsECoSitesChecked] = useState(false); // edotvo toggle
  const [isOpSitesChecked, setIsOpSitesChecked] = useState(false); // oprtr toggle
  const [isTopMarketShareChecked, setTopMarketShareChecked] = useState(true);
  
  const [selectedOperatorSupply, setSelectedOperatorSupply] = useState();
  const [selectedOperatorDemand, setSelectedOperatorDemand] = useState();
  const [isDemandNoDataChecked, setIsDemandNoDataChecked] = useState(true);
  const [demandScoreRange, setDemandScoreRange] = useState([0, 1]);
  const [locationFilters, setLocationFilters] = useState({
    country: null,
    year: null,
    quarter: null,
  });
  const [connectionTypes, setConnectionTypes] = useState({
    supply_best: [],
    supply_custom: [],
    demand: [],
  });
  const [dataTypes, setDataTypes] = useState({
    supply_best: [],
    supply_custom: [],
    demand: [],
  });
  const [aggrTypes, setAggrTypes] = useState({
    supply_best: [],
    supply_custom: [],
  });

  const [isCapacityChecked, setIsCapacityChecked] = useState(false);
  const [isCapacityVisible, setIsCapacityVisible] = useState(false);
  const [isMarketShareChecked, setIsMarketShareChecked] = useState(false);
  const [isMarketShareVisible, setIsMarketShareVisible] = useState(false);
  const [isIBSChecked, setIsIBSChecked] = useState(false);
  const [isBestIBSEnabled, setIsBestIBSChecked] = useState(true);
  const [isIBSVisible, setIsIBSVisible] = useState(false);
  const [isUploadFilesVisible, setIsUploadFilesVisible] = useState(false);
  const supplyPerms = selectedCountryPrems?.find((p) => p.name === "Supply");
  const demandPerms = selectedCountryPrems?.find((p) => p.name === "Demand");
  const capacityPerms = selectedCountryPrems?.find((p) => p.name === "Capacity");
  const ibsPerms = selectedCountryPrems?.find((p) => p.name === "IBS");
  const sitesPerms = selectedCountryPrems?.find((p) => p.name === "Sites");
  const msPerms = selectedCountryPrems?.find((p) => p.name === "Market Share");
  const uploadFilesPerms = selectedCountryPrems?.find((p) => p.name === "Upload Files");
  const bestOpPerm = supplyPerms?.sub_permissions?.find((p) => p.name === "Best Operator");
  const bestOpAggrPerm = bestOpPerm?.sub_permissions?.find((p) => p.name === "Aggregation Type");
  const opPerfPerm = supplyPerms?.sub_permissions?.find((p) => p.name === "Operator Performance");
  const opPerfAggrPerm = opPerfPerm?.sub_permissions?.find((p) => p.name === "Aggregation Type");
  const demandScorePerm = demandPerms?.sub_permissions?.find((p) => p.name === "Demand Score");
  const sampleScorePerm = demandPerms?.sub_permissions?.find((p) => p.name === "Sample Score");
  const deviceScorePerm = demandPerms?.sub_permissions?.find((p) => p.name === "Device Score");
  const edotcoSitePerm = sitesPerms?.sub_permissions?.find((p) => p.name === "E.Co Sites");
  const opSitePerm = sitesPerms?.sub_permissions?.find((p) => p.name === "Operator Sites");
  const ecoGAPerm = edotcoSitePerm?.sub_permissions?.find((p) => p.name === "Geographical Area Filter");
  const ecoCTPerm = edotcoSitePerm?.sub_permissions?.find((p) => p.name === "Connection Type");
  const ecoDTPerm = edotcoSitePerm?.sub_permissions?.find((p) => p.name === "Data Type");
  const exitingTenantsPerm = edotcoSitePerm?.sub_permissions?.find((p) => p.name === "Existing Tenants");
  const proposedTenantsPerm = edotcoSitePerm?.sub_permissions?.find((p) => p.name === "Proposed Tenants");
  const opGAPerm = opSitePerm?.sub_permissions?.find((p) => p.name === "Geographical Area Filter");
  const topMSPerm = msPerms?.sub_permissions?.find((p) => p.name === "Top Market Share");
  const topMSAggrPerm = topMSPerm?.sub_permissions?.find((p) => p.name === "Aggregation Type");
  const opMSPerm = msPerms?.sub_permissions?.find((p) => p.name === "Operator Market Share");
  const opMSAggrPerm = opMSPerm?.sub_permissions?.find((p) => p.name === "Aggregation Type");
  const opMSChartView = opMSPerm?.sub_permissions?.find((p) => p.name === "Chart View");
  const ibsChartPerm = ibsPerms?.sub_permissions?.find((p) => p.name === "Chart View");
  const bestIbsPerm = ibsPerms?.sub_permissions?.find((p) => p.name === "Best Operator");
  const opIbsPerm = ibsPerms?.sub_permissions?.find((p) => p.name === "Operator Performance");
  const [demandType, setDemandType] = useState("");
  const [isBestOpChecked, setIsBestOpChecked] = useState(true);
  const [operatorDataSupply, setOperatorDataSupply] = useState([]);
  const [operatorDataDemand, setOperatorDemand] = useState([]);
  const {
    onClickSupplyEyeIcon,
    appliedFilters,
    onChangeSupplyType,
    onChangeSupplyBestOpToggle,
    onChangeCustomOpSupply,
    onClickDemandEyeIcon,
    onChangeDemandScore,
    operatorDetails,
    onChangeDemandOperator,
    onChangeDemandNoDataToggle,
    onClickSiteEyeIcon,
    generateSiteLegend,
    onChangeLevelFilterToggle,
    supplyLvlFilters,
    supplyFilters,
    setSupplyFilters,
    setMinMaxSupplyOp,
    demandFilters,
    setDemandFilters,
    onChangeSupplyConnectionType,
    onChangeSupplyDataType,
    onChangeDemandConnectionType,
    onClickCapacityEyeIcon,
    onClickMarketShareEyeIcon,
    onClickIbsEyeIcon,
    ibsConfig,
    siteLegend,
    supplyLegend5G,
    supplyLegend4G,
    supplyLegend3G,
    demandLegend,
    capacityLegend,
    msConfig,
    proposedSiteVisible,
    setEnabledModules,
    filterOpPermissions,
    setPermittedOpsForDemand,
    mapPlacement,
    operatorsPerfPermittedSupply,
    resetSupplyToolTip,
    setMarketShareLegendData,
    setAffluenceLvlFilters
  } = useContext(WebMapContext);
  const prevDependenciesRef = useRef({});
  const lastDemandTypeRef = useRef("");

  useEffect(() => {
    setIsBestOpChecked(bestOpPerm?.is_enable);
  }, [bestOpPerm?.is_enable]);

  useEffect(() => {
    const prevDeps = prevDependenciesRef.current;
    const newDeps = { demandScorePerm, sampleScorePerm, deviceScorePerm, isDemandChecked };
    
    if (JSON.stringify(prevDeps) === JSON.stringify(newDeps)) {
      return; // Exit if dependencies are the same
    }
  
    prevDependenciesRef.current = newDeps;
    if (demandType === "") {
      if (demandScorePerm?.is_enable) {
        setDemandType(DemandScoreTypes.FINAL);
        lastDemandTypeRef.current = DemandScoreTypes.FINAL;
      } else if (sampleScorePerm?.is_enable) {
        setDemandType(DemandScoreTypes.SAMPLE);
        lastDemandTypeRef.current = DemandScoreTypes.SAMPLE;
        onChangeDemandScore(DemandScoreTypes.SAMPLE);
        handleOpsWithDemandType(DemandScoreTypes.SAMPLE);
      } else if (deviceScorePerm?.is_enable) {
        setDemandType(DemandScoreTypes.DEVICE);
        lastDemandTypeRef.current = DemandScoreTypes.DEVICE;
        onChangeDemandScore(DemandScoreTypes.DEVICE);
        handleOpsWithDemandType(DemandScoreTypes.DEVICE);
      } else {
        setDemandType("");
      }
    } else {
      if (lastDemandTypeRef.current === DemandScoreTypes.FINAL) {
        return;
      } else {
        setDemandType(lastDemandTypeRef.current);
        onChangeDemandScore(lastDemandTypeRef.current);
        handleOpsWithDemandType(lastDemandTypeRef.current);
      }
    }
  }, [demandScorePerm, sampleScorePerm, deviceScorePerm, isDemandChecked, appliedFilters.year, appliedFilters.quarter]); 

  useEffect(() => {
    const { country } = appliedFilters;
    const {country: currentCountry } = locationFilters;

    if (country == null || country !== currentCountry ) {

      if (isSupplyVisible) onClickSupplyEyeIcon(false);
      if (isDemandVisible) onClickDemandEyeIcon(false);
      if (isCapacityVisible) onClickCapacityEyeIcon(false);
      if (isMarketShareVisible) onClickMarketShareEyeIcon(false);
      if (isIBSVisible) onClickIbsEyeIcon(false);
      if (isSitesVisible) onClickSiteEyeIcon(false);

      setIsSupplyChecked(false);
      setIsDemandChecked(false);
      setIsCapacityChecked(false);
      setIsMarketShareChecked(false);
      setIsIBSChecked(false);
      setIsSitesChecked(false);
      setIsECoSitesChecked(false);
      setIsOpSitesChecked(false);

      setIsSupplyVisible(false);
      setIsDemandVisible(false);
      setIsCapacityVisible(false);
      setIsMarketShareVisible(false);
      setIsIBSVisible(false);
      setIsSitesVisible(false);

      setIsBestOpChecked(true);
      setSelectedOperatorSupply();
      setOperatorDemand([]);
      setPermittedOpsForDemand([]);
      setDemandType("");
    }

    const { best, custom } = supplyFilters.connectionType;
    if (best || custom)
      onChangeSupplyConnectionType({ best: null, custom: null });

  }, [appliedFilters.country]);
  
  useEffect(() => {
    const { country, year, quarter } = appliedFilters;
    const {
      country: currentCountry,
      year: currentYear,
      quarter: currentQuarter,
    } = locationFilters;
    if (
      country == null ||
      year == null ||
      quarter == null ||
      country !== currentCountry ||
      year !== currentYear ||
      quarter !== currentQuarter
    ) {
      // setIsSupplyChecked(false);
      // setIsDemandChecked(false);
      // setIsSitesChecked(false);
      // setIsECoSitesChecked(false);
      // setIsOpSitesChecked(false);
      // setIsCapacityChecked(false);
      // setIsMarketShareChecked(false);
      // setIsIBSChecked(false);

      // setIsSupplyVisible(false);
      // setIsDemandVisible(false);
      // setIsSitesVisible(false);
      // setIsCapacityVisible(false);
      // setIsMarketShareVisible(false);
      // setIsIBSVisible(false);
      // if (isSupplyVisible) onClickSupplyEyeIcon(false);
      // if (isDemandVisible) onClickDemandEyeIcon(false);
      // if (isSitesVisible) onClickSiteEyeIcon(false);
      // if (isCapacityVisible) onClickCapacityEyeIcon(false);
      // if (isMarketShareVisible) onClickMarketShareEyeIcon(false);
      // if (isIBSVisible) onClickIbsEyeIcon(false);
    }
    setLocationFilters(appliedFilters);
  }, [appliedFilters]);

  useEffect(() => {
    // reset best op toggle
    // setIsBestOpChecked(true);

    // reset connection types
    const { supply_best: sb, supply_custom: sc, demand: dmd } = connectionTypes;
    if (sb.length > 0 || sc.length > 0 || dmd.length > 0)
      setConnectionTypes({
        supply_best: [],
        supply_custom: [],
        demand: [],
      });
    setDataTypes({
      supply_best: [],
      supply_custom: [],
      demand: [],
    });

    // reset aggr type
    setAggrTypes({
        supply_best: [],
        supply_custom: [],
      });

    const { best, custom } = supplyFilters.connectionType;
    const { connectionType: d_ct } = demandFilters;
    if (best || custom)
      onChangeSupplyConnectionType({ best: null, custom: null });
    if (d_ct) onChangeDemandConnectionType(null);

    //turn off eye icon in supply/demand/site/capacity/market-share/ibs
    // setIsSupplyVisible(false);
    // setIsDemandVisible(false);
    // setIsSitesVisible(false);
    // setIsCapacityVisible(false);
    // setIsMarketShareVisible(false);
    // setIsIBSVisible(false);
  }, [appliedFilters.country, appliedFilters.year, appliedFilters.quarter]);

  useEffect(() => {
    generateSiteLegend(isSitesVisible, isOpSitesChecked, isECoSitesChecked);
    // eslint-disable-next-line
  }, [isECoSitesChecked, isOpSitesChecked, isSitesVisible]);


  useEffect(() => {
    if (!selectedOperatorSupply) {
      setSelectedOperatorSupply(operatorsPerfPermittedSupply[0]?.operator)
    }
    setOperatorDataSupply(operatorsPerfPermittedSupply);
  }, [isSupplyChecked, operatorsPerfPermittedSupply]);

  useEffect(() => {
    setSelectedOperatorSupply(operatorsPerfPermittedSupply[0]?.operator);

    if(demandType === DemandScoreTypes.AFFLUENCE){
      getAffluenceLvlFilters();
    }

    const fetchOperators = async () => {
      const type = demandType === DemandScoreTypes.SAMPLE 
        ? DemandScoreLabels.SAMPLE 
        : demandType === DemandScoreTypes.DEVICE 
        ? DemandScoreLabels.DEVICE 
        : null;
      if (!type) return;
      await handleOpsWithDemandType(demandType);
    };
  
    fetchOperators();
  }, [appliedFilters.year, appliedFilters.quarter, demandType, operatorsPerfPermittedSupply]);
  

  const handleOpsWithDemandType = async (demandType) => {
    const type = demandType === DemandScoreTypes.SAMPLE ? DemandScoreLabels.SAMPLE : demandType === DemandScoreTypes.DEVICE ? DemandScoreLabels.DEVICE : null;
    if (!isDemandChecked || !type) return;
    const permitedOperators = await filterOpPermissions(
      appliedFilters.countryCode,
      Names_For_Filter_OPs.DEMAND,
      type
    );
    const filteredOperators = permitedOperators?.filter(operator => operator.type === "MNO");
    if (filteredOperators?.length) {
      const operator = filteredOperators[0]?.operator;
  
      setSelectedOperatorDemand(operator);
      setDemandFilters(prevFilters => ({
        ...prevFilters,
        operator
      }));
      setOperatorDemand(filteredOperators);
      setPermittedOpsForDemand(filteredOperators);
    }
  };
  
  const toggleTopMarketShare = () => { setTopMarketShareChecked(!isTopMarketShareChecked) }

  useEffect(() => {
    if (
      isSupplyChecked ||
      isDemandChecked ||
      isSitesChecked ||
      isCapacityChecked ||
      isMarketShareChecked ||
      isIBSChecked
    ) {
      setOpenedTabs({ isAdminLevelCardOpen: false, isSidePanelCardOpen: true });
    }
  }, [isSupplyChecked, isDemandChecked, isSitesChecked, isCapacityChecked, isMarketShareChecked, isIBSChecked]);
  
  // Enabled modules for download feature
  useEffect(() => {
    const updateModule = (condition, moduleKey) => {
      setEnabledModules((prevModules) => ({
        ...prevModules,
        [moduleKey]: condition,
      }));
    };

    updateModule(isDemandVisible, Modules.DEMAND);
    updateModule(isCapacityVisible, Modules.CAPACITY);
    updateModule(isSupplyVisible && isBestOpChecked, Modules.SUPPLY_BEST_OPERATOR);
    updateModule(isSupplyVisible && !isBestOpChecked, Modules.SUPPLY_OPERATOR_PERFORMANCE);
    updateModule(isSitesVisible && isECoSitesChecked, Modules.SITES_EDOTCO);
    updateModule(isSitesVisible && isOpSitesChecked, Modules.SITES_OPERATOR);
    updateModule(proposedSiteVisible && isECoSitesChecked, Modules.SITES_PROPOSED);
    updateModule(isMarketShareVisible && isTopMarketShareChecked, Modules.MARKET_SHARE_TOP);
    updateModule(isMarketShareVisible && !isTopMarketShareChecked, Modules.MARKET_SHARE_OPERATOR);
    updateModule(isIBSVisible && isBestIBSEnabled, Modules.IBS_BEST);
    updateModule(isIBSVisible && !isBestIBSEnabled, Modules.IBS_OPERATOR);
  }, [
    isSupplyChecked,
    isBestIBSEnabled,
    isBestOpChecked,
    isECoSitesChecked,
    isOpSitesChecked,
    isTopMarketShareChecked,
    isIBSVisible,
    setEnabledModules,
    isDemandVisible,
    isCapacityVisible,
    isSupplyVisible,
    isSitesVisible,
    isMarketShareVisible,
    proposedSiteVisible
  ]);

  useEffect(() => {
    if (openedTabs.isAdminLevelCardOpen) {
      setIsSupplyChecked(false);
      setIsDemandChecked(false);
      setIsSitesChecked(false);
      setIsCapacityChecked(false);
      setIsMarketShareChecked(false);
      setIsIBSChecked(false);
    }
  }, [openedTabs]);

  useEffect(() => {
    if (
      connectionTypes.supply_best.length > 0 &&
      !supplyFilters.connectionType.best
    ) {
      onChangeSupplyConnectionType({
        ...supplyFilters.connectionType,
        best: connectionTypes.supply_best[0].value,
      });
    }
    if (dataTypes.supply_best.length > 0 && !supplyFilters.dataType.best) {
      onChangeSupplyDataType({
        ...supplyFilters.dataType,
        best: dataTypes.supply_best[0].value,
      });
    }
  }, [connectionTypes.supply_best, dataTypes.supply_best]);

  //fetch data types
  useEffect(() => {
    if (supplyFilters.connectionType.best)
      fetchDataTypes(
        "best_operator_statistics",
        "supply_best",
        supplyFilters.connectionType.best
      );
  }, [supplyFilters.connectionType.best]);

  useEffect(() => {
    if (supplyFilters.connectionType.custom && !supplyFilters.isBestOperator)
      fetchDataTypes(
        "operator_statistics",
        "supply_custom",
        supplyFilters.connectionType.custom
      );
  }, [supplyFilters.connectionType.custom]);

  useEffect(() => {
    if (
      connectionTypes.demand?.length > 0 &&
      demandFilters.connectionType &&
      demandFilters.demandType !== DemandScoreTypes.FINAL
    ) {
      fetchDataTypes(
        "demand_operator_statistics",
        "demand",
        demandFilters.connectionType
      );
    } else {
      setDataTypes((prevState) => ({ ...prevState, demand: [] }));
    }
  }, [demandFilters.connectionType, connectionTypes.demand]);

  //fetch aggr types
  useEffect(() => {
    if(supplyFilters.isBestOperator && bestOpAggrPerm?.is_enable === true){
      fetchAggrTypes(
            "best_operator_statistics",
            "supply_best",
            supplyFilters.connectionType.best
          );
    }else if(opPerfAggrPerm?.is_enable === true){
      fetchAggrTypes(
            "operator_statistics",
            "supply_custom",
            supplyFilters.connectionType.custom
          );
    }
  }, [supplyFilters.connectionType.best, supplyFilters.connectionType.custom, supplyFilters.isBestOperator])

  //fetch min max for data related legend for the first time if user comes from best data related filters
  useEffect(() => {
    if (
      !supplyFilters.isBestOperator &&
      ![SupplyType.RSRP, SupplyType.DOWNLOAD, SupplyType.UPLOAD].includes(
        supplyFilters.supplyType
      ) &&
      supplyFilters.dataType.custom &&
      supplyFilters.connectionType.custom
    ) {
      getMinMaxSupplyOp(supplyFilters.supplyType);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    supplyFilters.connectionType.custom,
    supplyFilters.dataType.custom,
    supplyFilters.isBestOperator,
    supplyFilters.supplyType,
    supplyFilters.operator
  ]);

  const sectionsVisibilty = useMemo(() => {

    const sections = {

      supply: (supplyLegend3G && supplyLegend3G.length > 0) ||
        (supplyLegend4G && supplyLegend4G.length > 0) ||
        (supplyLegend5G && supplyLegend5G.length > 0),
      demand: demandLegend && demandLegend.length > 0,
      ibs: ibsConfig && Object.values(ibsConfig).some(array => array?.length > 0),
      markeShare: msConfig && msConfig.length > 0,
      capacity: capacityLegend && capacityLegend.length > 0,
      site: siteLegend && siteLegend.length > 0,
    };
    return sections;

  }, [ibsConfig, supplyLegend3G, supplyLegend4G, supplyLegend5G, demandLegend, capacityLegend, siteLegend, msConfig]);

  useEffect(() =>{
    if(!sectionsVisibilty.markeShare) setMarketShareLegendData([])
  }, [sectionsVisibilty.markeShare])

  const supplySidePanelOnClick = () => {
    setIsSupplyChecked((isSupplyChecked) => !isSupplyChecked);
    if (isDemandChecked) setIsDemandChecked(!isDemandChecked);
    if (isSitesChecked) setIsSitesChecked(!isSitesChecked);
    if (isCapacityChecked) setIsCapacityChecked(!isCapacityChecked);
    if (isMarketShareChecked) setIsMarketShareChecked(!isMarketShareChecked);
    if (isIBSChecked) setIsIBSChecked(!isIBSChecked);
  };

  const demandSidePanelOnClick = () => {
    setIsDemandChecked((isDemandChecked) => !isDemandChecked);
    if (isSitesChecked) setIsSitesChecked(!isSitesChecked);
    if (isSupplyChecked) setIsSupplyChecked(!isSupplyChecked);
    if (isCapacityChecked) setIsCapacityChecked(!isCapacityChecked);
    if (isMarketShareChecked) setIsMarketShareChecked(!isMarketShareChecked);
    if (isIBSChecked) setIsIBSChecked(!isIBSChecked);
  };

  const siteSidePanelOnClick = () => {
    setIsSitesChecked((isSitesChecked) => !isSitesChecked);
    if (isDemandChecked) setIsDemandChecked(!isDemandChecked);
    if (isSupplyChecked) setIsSupplyChecked(!isSupplyChecked);
    if (isCapacityChecked) setIsCapacityChecked(!isCapacityChecked);
    if (isMarketShareChecked) setIsMarketShareChecked(!isMarketShareChecked);
    if (isIBSChecked) setIsIBSChecked(!isIBSChecked);
  };

  const capacitySidePanelOnClick = () => {
    setIsCapacityChecked((isCapacityChecked) => !isCapacityChecked);
    if (isDemandChecked) setIsDemandChecked(!isDemandChecked);
    if (isSitesChecked) setIsSitesChecked(!isSitesChecked);
    if (isSupplyChecked) setIsSupplyChecked(!isSupplyChecked);
    if (isMarketShareChecked) setIsMarketShareChecked(!isMarketShareChecked);
    if (isIBSChecked) setIsIBSChecked(!isIBSChecked);
  };

  const marketShareSidePanelOnClick = () => {
    setIsMarketShareChecked((isMarketShareChecked) => !isMarketShareChecked);
    if (isSupplyChecked) setIsSupplyChecked(!isSupplyChecked);
    if (isDemandChecked) setIsDemandChecked(!isDemandChecked);
    if (isSitesChecked) setIsSitesChecked(!isSitesChecked);
    if (isCapacityChecked) setIsCapacityChecked(!isCapacityChecked);
    if (isIBSChecked) setIsIBSChecked(!isIBSChecked);
  };

  const ibsSidePanelOnClick = () => {
    setIsIBSChecked(!isIBSChecked);
    if (isSupplyChecked) setIsSupplyChecked(!isSupplyChecked);
    if (isDemandChecked) setIsDemandChecked(!isDemandChecked);
    if (isSitesChecked) setIsSitesChecked(!isSitesChecked);
    if (isCapacityChecked) setIsCapacityChecked(!isCapacityChecked);
    if (isMarketShareChecked) setIsMarketShareChecked(!isMarketShareChecked);
  };

  const uploadSidePanelOnClick = () => {
    setIsUploadFilesVisible(!isUploadFilesVisible);
  };

  const handleECoSitesToggle = async (event) => {
    setIsECoSitesChecked(event.target.checked);
  };

  const handleOpSitesToggle = (event) => {
    setIsOpSitesChecked(event.target.checked);
  };

  const handleBestOpChange = (checked) => {
    setIsBestOpChecked(checked);
    onChangeSupplyBestOpToggle(checked);
  };

  const onClickSupplyVisibility = () => {
    if (
      !isSupplyVisible &&
      !supplyFilters.connectionType.best &&
      connectionTypes.supply_best.length === 0
    ) {
      // getting initial connection types
      fetchConnectionTypes("best_operator_statistics", "supply_best");
    }
    if (isDemandVisible) setIsDemandVisible(false);
    if (isCapacityVisible) setIsCapacityVisible(false);
    if (isMarketShareVisible) setIsMarketShareVisible(false);
    if (isIBSVisible) setIsIBSVisible(false);
    toggleVisibility(setIsSupplyVisible, isSupplyVisible);
    onClickSupplyEyeIcon(!isSupplyVisible);
  };

  const onChangeSupplyOperator = (val) => {
    setSelectedOperatorSupply(val);
    fetchConnectionTypes("operator_statistics", "supply_custom", { op: val });
    if (!isBestOpChecked) onChangeCustomOpSupply(val);
  };
  
  const toggleVisibility = (setter, isActive) => {
    setter(!isActive);
  };

  const onClickDemandVisibility = () => {
    if (isSupplyVisible) setIsSupplyVisible(false);
    if (isCapacityVisible) setIsCapacityVisible(false);
    if (isMarketShareVisible) setIsMarketShareVisible(false);
    if (isIBSVisible) setIsIBSVisible(false);
    toggleVisibility(setIsDemandVisible, isDemandVisible);
    onClickDemandEyeIcon(!isDemandVisible);
  };

  const onChangDemandOperator = (val) => {
    setSelectedOperatorDemand(val);
    onChangeDemandOperator(val);
    fetchConnectionTypes("demand_operator_statistics", "demand", { op: val });
  };

  const onClickSitesVisibility = () => {
    if (isEyeIconVisibleAsGreenForSites) {
      toggleVisibility(setIsSitesVisible, isSitesVisible);
      onClickSiteEyeIcon(!isSitesVisible);
    }
  };

  const onClickCapacityVisibility = () => {
    if (isSupplyVisible) setIsSupplyVisible(false);
    if (isDemandVisible) setIsDemandVisible(false);
    if (isMarketShareVisible) setIsMarketShareVisible(false);
    if (isIBSVisible) setIsIBSVisible(false);
    toggleVisibility(setIsCapacityVisible, isCapacityVisible);
    onClickCapacityEyeIcon(!isCapacityVisible);
  };

  const onClickMarketShareVisibility = () => {
    if (isSupplyVisible) setIsSupplyVisible(false);
    if (isDemandVisible) setIsDemandVisible(false);
    if (isCapacityVisible) setIsCapacityVisible(false);
    if (isIBSVisible) setIsIBSVisible(false);
    toggleVisibility(setIsMarketShareVisible, isMarketShareVisible);
    onClickMarketShareEyeIcon(!isMarketShareVisible);
  };

  const onClickIBSVisibility = () => {
    if (isSupplyVisible) setIsSupplyVisible(false);
    if (isDemandVisible) setIsDemandVisible(false);
    if (isCapacityVisible) setIsCapacityVisible(false);
    if (isMarketShareVisible) setIsMarketShareVisible(false);
    toggleVisibility(setIsIBSVisible, isIBSVisible);
    onClickIbsEyeIcon(!isIBSVisible);
  };

  const fetchConnectionTypes = async (table, type, query = {}) => {
      try {
        const { countryCode, year, quarter } = appliedFilters;
        if (countryCode && year && quarter) {
          const { data } = await getFilters({
            countryCode,
            year,
            quarter,
            column: "connection_type",
            table,
            hint: table === 'operator_statistics' ? "SUPPLY_OP_CT" : table === 'best_operator_statistics' ? "SUPPLY_BEST_CT" : table === 'demand_operator_statistics' ? "DEMAND_OP_CT" : null
          },
            {
              ...((table === 'operator_statistics' || table === 'demand_operator_statistics') && {
                params: {
                  filters: `operator:${query?.op || selectedOperatorSupply}`,
                },
              })
            }
          );
          if (data) {
            const connections = data.map((x) => ({ label: x._id, value: x._id }));
            connections.sort((a, b) => b.label?.charAt(0) - a.label?.charAt(0));
            setConnectionTypes({ ...connectionTypes, [type]: connections });
          }
        }
      } catch (err) {
        console.log("Error while fetching connection types: ", err);
      }
    };

  const fetchDataTypes = async (table, type, cType) => {
    try {
      const { countryCode, year, quarter } = appliedFilters;
      if (countryCode && year && quarter && cType) {
        const { data } = await getFilters(
          {
            countryCode,
            year,
            quarter,
            column: "data_type",
            table,
            hint: table === 'operator_statistics' ? "SUPPLY_OP_DT" : table === 'best_operator_statistics' ? "SUPPLY_BEST_CT" : table === 'demand_operator_statistics' ? "DEMAND_OP_DT" : null
          },
          {
            params: {
              filters: `connection_type:${cType}`,
            },
          }
        );
        if (data) {
          const opTypes = data.map((x) => ({ label: x._id, value: x._id }));
          setDataTypes((prevState) => ({ ...prevState, [type]: opTypes }));
        }
      }
    } catch (err) {
      console.log("Error while fetching data types:", err);
      return null;
    }
  };

  const fetchAggrTypes = async (table, type, cType) => {
    try {
      const { countryCode, year, quarter } = appliedFilters;
      if (countryCode && year && quarter && cType) {
        const { data } = await getFilters(
          {
            countryCode,
            year,
            quarter,
            column: "agg_type",
            table,
            hint:
              table === "operator_statistics"
                ? "SUPPLY_OP_AGGR"
                : table === "best_operator_statistics"
                ? "SUPPLY_BEST_AGGR"
                : null,
          },
          {
            params: {
              filters: `connection_type:${cType}`,
            },
          }
        );

        if (data) {
          const agTypes = data.map((x) => ({ label: x._id, value: x._id }));
          setAggrTypes((prevState) => ({ ...prevState, [type]: agTypes }));

          if (
            [SupplyType.RSRP, SupplyType.DOWNLOAD, SupplyType.UPLOAD].includes(
              supplyFilters.supplyType
            )
          ) {
            if (type === "supply_best") {
              setSupplyFilters((prevFilters) => ({
                ...prevFilters,
                aggrType: { best: agTypes[0]?.value, custom: null },
              }));
            } else if (type === "supply_custom") {
              setTimeout(() => {
                setSupplyFilters((prevFilters) => ({
                  ...prevFilters,
                  aggrType: { best: null, custom: agTypes[0]?.value },
                }));
              }, 0);
            }
          }
        }
      }
    } catch (err) {
      console.log("Error while fetching aggr types: ", err);
    }
  };

  const getMinMaxSupplyOp = async (type) => {
      try {
        const { countryCode, year, quarter } = appliedFilters;
    
        const { data } = await fetchMinMaxSupplyOp({
          country: countryCode,
          quarter: `${quarter} ${year}`,
          ct: supplyFilters.connectionType.custom,
          dt: supplyFilters.dataType.custom,
          op: supplyFilters.operator,
          st: type,
        });
  
        if (data) {           
          setMinMaxSupplyOp((prevState) => ({
            ...prevState, 
            [type]: data, 
          }));
        }
      } catch (e) {
        console.log("Fetch gradient min max failed: ", e);
      }
    };

    const getAffluenceLvlFilters = async () => {
      try {
        const { data } = await getFilters(
          {
            countryCode: appliedFilters.countryCode,
            year: appliedFilters.year,
            quarter: appliedFilters.quarter,
            column: "level",
            table: "demand_operator_statistics",
            hint: "DEMAND_OP_AFFLUENCE",
          },
          {
            params: {},
          }
        );
        if (data) {
          let filters = data.map((item) => ({
            filterName: item?._id,
            isChecked: true,
            key: item?._id,
          }));
          setAffluenceLvlFilters(filters);
        }
      } catch (err) {
        console.log(err);
      }
    }
  
  return (
    <Context.Provider value={{ value, setValue }}>
      <div hidden={!show} style={{ "overflow-y": "auto", width: "100%" }}>
        {supplyPerms?.is_enable === true && (
          <>
            {sectionsVisibilty.supply ? (
              <>
                <SidePanelCard
                  title="Supply"
                  icon={
                    isSupplyChecked
                      ? "/assets/supply_white.png"
                      : "/assets/wireless.png"
                  }
                  nonShow={true}
                  clicked={isSupplyChecked}
                  onClick={supplySidePanelOnClick}
                  isVisibleAsGreen={true}
                  visibilityClicked={isSupplyVisible}
                  onClickVisibility={onClickSupplyVisibility}
                />
                {isSupplyChecked && (
                  <Grid
                    container
                    item
                    style={{
                      justifyContent: "space-between",
                      textAlign: "left",
                      font: "normal normal normal 12px/16px Open Sans",
                      letterSpacing: "0px",
                      color: "black",
                      opacity: "1",
                      padding: "10px",
                    }}
                  >
                    {bestOpPerm?.is_enable === true && (
                      <>
                        <div
                          style={{
                            width: "100%",
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <ToggleTitleWrapper main>
                            Best Operator
                          </ToggleTitleWrapper>
                          <ToggleSwitch
                            checked={isBestOpChecked}
                            onChange={(e) =>
                              handleBestOpChange(e.target.checked)
                            }
                          />
                        </div>
                        {isBestOpChecked && (
                          <Grid
                            container
                            item
                            style={{
                              justifyContent: "space-between",
                              margin: "15px 5px 0px",
                              padding: "10px",
                              font: "normal normal 550 13px/18px Open Sans",
                              background: "#FFFFFF 0% 0% no-repeat padding-box",
                              border: "1px solid #007842",
                              borderRadius: "5px",
                              opacity: "1",
                              marginBottom: "10px",
                            }}
                          >
                            <WebMapConnectionType
                              type={
                                isBestOpChecked
                                  ? "supply_best"
                                  : "supply_custom"
                              }
                              data={
                                connectionTypes[
                                  isBestOpChecked
                                    ? "supply_best"
                                    : "supply_custom"
                                ]
                              }
                              fetchHandler={fetchConnectionTypes}
                            />
                            <Grid style={{ paddingTop: "10px", width: "100%" }}>
                              <WebMapDataType
                                type={
                                  isBestOpChecked
                                    ? "supply_best"
                                    : "supply_custom"
                                }
                                data={
                                  dataTypes[
                                    isBestOpChecked
                                      ? "supply_best"
                                      : "supply_custom"
                                  ]
                                }
                                setDataTypes={setDataTypes}
                              />
                            </Grid>
                            {isBestOpChecked &&
                            connectionTypes.supply_best.length > 0 ? (
                              <>
                                <div style={{ marginTop: "30px" }}></div>
                                <HorizontalSeparator />
                              </>
                            ) : (
                              <></>
                            )}

                            {aggrTypes.supply_best?.length > 0 && bestOpAggrPerm?.is_enable === true && (
                              <CommonTypeTabsGroup
                                title="Aggregation Type"
                                data={aggrTypes.supply_best}
                                value={supplyFilters?.aggrType?.best}
                                handleChange={(val) => {
                                  resetSupplyToolTip();
                                  if (
                                    supplyFilters.supplyType === SupplyType.DATA_RECEIVED ||
                                    supplyFilters.supplyType === SupplyType.DATA_TRANSMIT ||
                                    supplyFilters.supplyType === SupplyType.DATA_TRANSCEIVED
                                  ) {
                                    setSupplyFilters((prevFilters) => ({
                                      ...prevFilters,
                                      aggrType: { best: val, custom: null },
                                      supplyType: SupplyType.RSRP,
                                    }));
                                  } else {
                                    setSupplyFilters((prevFilters) => ({
                                      ...prevFilters,
                                      aggrType: { best: val, custom: null },
                                    }));
                                  }
                                }}
                                icon={false}
                              />
                            )}

                            <RadioSelectGroup
                              key={"radioSelectGroup" + value}
                              showLabelId={false}
                              labelId="Supply Type"
                              name="Supply Type"
                              showLabel1={true}
                              label1={SupplyType.RSRP}
                              showLabel2={true}
                              label2={SupplyType.DOWNLOAD}
                              showLabel3={true}
                              label3={SupplyType.UPLOAD}
                              value={supplyFilters?.supplyType}
                              setSelectedValue={(e) => {
                                onChangeSupplyType(e);

                                if (
                                  (e === SupplyType.RSRP ||
                                  e === SupplyType.DOWNLOAD ||
                                  e === SupplyType.UPLOAD) &&
                                  (!supplyFilters.aggrType.best)

                                ) {
                                  setSupplyFilters((prevFilters) => ({
                                    ...prevFilters,
                                    supplyType: e,
                                    aggrType: {
                                      best:
                                        aggrTypes.supply_best[0]?.value || null,
                                      custom: null,
                                    },
                                  }));
                                } else {
                                  setSupplyFilters((prevFilters) => ({
                                    ...prevFilters,
                                    supplyType: e,
                                  }));
                                }
                              }}
                            />

                            <>
                              <div style={{ marginTop: "30px" }}></div>
                              <HorizontalSeparator />
                            </>

                            <RadioSelectGroup
                              showLabelId={false}
                              labelId="Transmission Type"
                              name="Transmission Type"
                              icon1={"/assets/networkActionIcon.png"}
                              showLabel1={true}
                              label1={SupplyType.DATA_RECEIVED}
                              icon2={"/assets/networkActionIcon.png"}
                              showLabel2={true}
                              label2={SupplyType.DATA_TRANSCEIVED}
                              icon3={"/assets/networkActionIcon.png"}
                              showLabel3={true}
                              label3={SupplyType.DATA_TRANSMIT}
                              value={supplyFilters?.supplyType}
                              setSelectedValue={(e) => {
                                onChangeSupplyType(e);

                                setSupplyFilters((prevFilters) => ({
                                  ...prevFilters,
                                  aggrType: { best: null, custom: null },
                                }));
                              }}
                            />
                          </Grid>
                        )}
                      </>
                    )}
                    {opPerfPerm?.is_enable === true && (
                      <>
                        {!isBestOpChecked && (
                          <>
                          {bestOpPerm?.is_enable === true && (<HorizontalSeparator />)}
                            <ToggleTitleWrapper main>
                              Operator Performance
                            </ToggleTitleWrapper>
                            <ToggleSwitch
                              checked={!isBestOpChecked}
                            onChange={(e) => handleBestOpChange(!e.target.checked)}
                            />

                            <SitesDetailsTile
                              title="Operators"
                              selectedOperator={selectedOperatorSupply}
                              onchangeOperator={onChangeSupplyOperator}
                              permitedOperatorData={operatorDataSupply}
                            />
                            <Grid
                              container
                              item
                              style={{
                                justifyContent: "space-between",
                                margin: "15px 5px 0px",
                                padding: "10px",
                                font: "normal normal 550 13px/18px Open Sans",
                                background: "#FFFFFF 0% 0% no-repeat padding-box",
                                border: "1px solid #007842",
                                borderRadius: "5px",
                                opacity: "1",
                                marginBottom: "10px",
                              }}
                            >
                              <WebMapConnectionType
                              type={isBestOpChecked ? "supply_best" : "supply_custom"}
                                data={
                                  connectionTypes[
                                isBestOpChecked ? "supply_best" : "supply_custom"
                                  ]
                                }
                                fetchHandler={fetchConnectionTypes}
                              />
                            <Grid style={{ paddingTop: "10px", width: "100%" }}>
                                <WebMapDataType
                                type={isBestOpChecked ? "supply_best" : "supply_custom"}
                                  data={
                                  dataTypes[isBestOpChecked ? "supply_best" : "supply_custom"]
                                  }
                                  setDataTypes={setDataTypes}
                                />
                              </Grid>
                            {(!isBestOpChecked && connectionTypes.supply_custom.length > 0) ? (
                                <>
                                  <div style={{ marginTop: "30px" }}></div>
                                  <HorizontalSeparator />
                                </>
                              ) : (
                                <></>
                              )}

                              {aggrTypes.supply_custom?.length > 0 && opPerfAggrPerm?.is_enable === true &&(
                                <>
                                  <CommonTypeTabsGroup
                                    title="Aggregation Type"
                                    data={aggrTypes.supply_custom}
                                    value={supplyFilters?.aggrType?.custom}
                                    handleChange={(val) => {
                                      resetSupplyToolTip();
                                      if (
                                        supplyFilters.supplyType === SupplyType.DATA_RECEIVED ||
                                        supplyFilters.supplyType === SupplyType.DATA_TRANSMIT ||
                                        supplyFilters.supplyType === SupplyType.DATA_TRANSCEIVED
                                      ) {
                                        setSupplyFilters((prevFilters) => ({
                                          ...prevFilters,
                                          aggrType: { best: null, custom: val },
                                          supplyType: SupplyType.RSRP,
                                        }));
                                      } else {
                                        setSupplyFilters((prevFilters) => ({
                                          ...prevFilters,
                                          aggrType: { best: null, custom: val },
                                        }));
                                      }
                                    }}
                                    icon={false}
                                  />
                                </>
                              )}

                              <RadioSelectGroup
                                key={"radioSelectGroup" + value}
                                showLabelId={false}
                                labelId="Supply Type"
                                name="Supply Type"
                                showLabel1={true}
                                label1={SupplyType.RSRP}
                                showLabel2={true}
                                label2={SupplyType.DOWNLOAD}
                                showLabel3={true}
                                label3={SupplyType.UPLOAD}
                                value={supplyFilters?.supplyType}
                                setSelectedValue={(e) => {
                                  onChangeSupplyType(e);

                                  if (
                                    (e === SupplyType.RSRP ||
                                    e === SupplyType.DOWNLOAD ||
                                    e === SupplyType.UPLOAD) &&
                                    (!supplyFilters.aggrType.custom)
    
                                  ) {
                                    setSupplyFilters((prevFilters) => ({
                                      ...prevFilters,
                                      supplyType: e,
                                      aggrType: {
                                        best: null,
                                        custom:
                                          aggrTypes.supply_custom[0]?.value ||
                                          null,
                                      },
                                    }));
                                  } else {
                                    setSupplyFilters((prevFilters) => ({
                                      ...prevFilters,
                                      supplyType: e,
                                    }));
                                  }
                                }}
                              />

                              <>
                                <div style={{ marginTop: "30px" }}></div>
                                <HorizontalSeparator />
                              </>

                              <RadioSelectGroup
                                showLabelId={false}
                                labelId="Transmission Type"
                                name="Transmission Type"
                                icon1={"/assets/networkActionIcon.png"}
                                showLabel1={true}
                                label1={SupplyType.DATA_RECEIVED}
                                icon2={"/assets/networkActionIcon.png"}
                                showLabel2={true}
                                label2={SupplyType.DATA_TRANSCEIVED}
                                icon3={"/assets/networkActionIcon.png"}
                                showLabel3={true}
                                label3={SupplyType.DATA_TRANSMIT}
                                value={supplyFilters?.supplyType}
                                setSelectedValue={ async (e) => {
                                  onChangeSupplyType(e);
                                  setSupplyFilters((prevFilters) => ({
                                    ...prevFilters,
                                    aggrType: { best: null, custom: null },
                                  }));
                                }}
                              />
                              {supplyLvlFilters[supplyFilters?.supplyType]
                                ?.length > 0 ? (
                                <>
                                  <HorizontalSeparator />
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      width: "100%",
                                    }}
                                  >
                                    {supplyLvlFilters[
                                      supplyFilters?.supplyType
                                    ]?.map((item) => {
                                      return (
                                        <div
                                          style={{
                                            display: "flex",
                                            width: "100%",
                                          }}
                                        >
                                          <div
                                            style={{
                                              display: "flex",
                                              justifyContent: "flex-start",
                                              width: "50%",
                                            }}
                                          >
                                            <ToggleTitleWrapper main>
                                              {item.filterName}
                                            </ToggleTitleWrapper>
                                          </div>
                                          <div
                                            style={{
                                              display: "flex",
                                              justifyContent: "flex-end",
                                              width: "50%",
                                            }}
                                          >
                                            <ToggleSwitch
                                              checked={item.isChecked}
                                              onChange={(e) => {
                                                onChangeLevelFilterToggle(item);
                                              }}
                                            />
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </>
                              ) : null}
                            </Grid>
                          </>
                        )}
                      </>
                    )}
                    {opPerfPerm?.is_enable === true && (
                      <>
                        {isBestOpChecked && (
                          <>
                          {bestOpPerm?.is_enable === true && (<HorizontalSeparator />)}
                            <ToggleTitleWrapper main>
                              Operator Performance
                            </ToggleTitleWrapper>
                            <ToggleSwitch
                              checked={!isBestOpChecked}
                            onChange={(e) => handleBestOpChange(!e.target.checked)}
                            />
                          </>
                        )}
                      </>
                    )}
                  </Grid>
                )}
              </>
            ) : (
              <></>
            )}
          </>
        )}

        {demandPerms?.is_enable === true && (
          <>
            {sectionsVisibilty.demand ? (
              <>
          
                <SidePanelCard
                  title="Demand"
                  icon={
                    isDemandChecked
                      ? "/assets/barchart_white.png"
                      : "/assets/barchart.png"
                  }
                  nonShow={true}
                  clicked={isDemandChecked}
                  onClick={demandSidePanelOnClick}
                  isVisibleAsGreen={true}
                  visibilityClicked={isDemandVisible}
                  onClickVisibility={onClickDemandVisibility}
                />
                {isDemandChecked && (
                  <Grid
                    container
                    item
                    style={{
                      textAlign: "left",
                      font: "normal normal 550 13px/18px Open Sans",
                      letterSpacing: "0px",
                      color: "black",
                      opacity: "1",
                      padding: "10px",
                    }}
                  >
                    <Grid
                      container
                      item
                      style={{
                        margin: "10px 4px 4px",
                        padding: "10px",
                        background: "#FFFFFF 0% 0% no-repeat padding-box",
                        border: "1px solid #007842",
                        borderRadius: "4px",
                        opacity: "1",
                      }}
                    >
                      <RadioSelectGroup
                        showLabelId={false}
                        labelId="Demand Score"
                        name="Demand Score"
                        showBorder={true}
                        showLabel1={demandScorePerm?.is_enable}
                    label1={demandScorePerm?.is_enable ? DemandScoreTypes.FINAL : ""}
                    labelOverride1={demandScorePerm?.is_enable ? DemandScoreLabels.FINAL : ""}
                    icon1={demandScorePerm?.is_enable ? "/assets/finalscore.png" : ""}
                        showLabel2={sampleScorePerm?.is_enable}
                    label2={sampleScorePerm?.is_enable? DemandScoreTypes.SAMPLE : ""}
                    labelOverride2={sampleScorePerm?.is_enable ? DemandScoreLabels.SAMPLE : ""}
                    icon2={sampleScorePerm?.is_enable ? "/assets/samplescore.png" : ""}
                        showLabel3={deviceScorePerm?.is_enable}
                    label3={deviceScorePerm?.is_enable ? DemandScoreTypes.DEVICE : ""}
                    labelOverride3={deviceScorePerm?.is_enable ? DemandScoreLabels.DEVICE : ""}
                    icon3={deviceScorePerm?.is_enable ? "/assets/devicescore.png" : ""}
                    showLabel4={deviceScorePerm?.is_enable}
                    label4={deviceScorePerm?.is_enable ? DemandScoreTypes.AFFLUENCE : ""}
                    labelOverride4={deviceScorePerm?.is_enable ? DemandScoreLabels.AFFLUENCE : ""}
                    icon4={deviceScorePerm?.is_enable ? "/assets/affluence.png" : ""}
                    value={demandType}
                    setSelectedValue={(e) => {
                      setDemandType(e);
                      onChangeDemandScore(e);
                      handleOpsWithDemandType(e)
                    }}
                  />
                </Grid>
                {demandType !== DemandScoreTypes.FINAL && demandType !== DemandScoreTypes.AFFLUENCE  ? (
                  <>
                    <SitesDetailsTile
                      title="Operators"
                      selectedOperator={selectedOperatorDemand}
                      onchangeOperator={onChangDemandOperator}
                      permitedOperatorData={operatorDataDemand}
                    />
                  </>
                ):null}
                {demandType !== DemandScoreTypes.AFFLUENCE ? (
                      <Grid
                        container
                        item
                        style={{
                          margin: "10px 4px 0px",
                          padding: "10px",
                          background: "#FFFFFF 0% 0% no-repeat padding-box",
                          border: "1px solid #007842",
                          borderRadius: "4px",
                          opacity: "1",
                        }}
                      >
                        {demandType !== DemandScoreTypes.FINAL ? (
                          <>
                            <WebMapConnectionType
                              type={"demand"}
                              data={connectionTypes["demand"]}
                              fetchHandler={fetchConnectionTypes}
                            />
                            <div style={{ marginTop: "10px", width: "100%" }}>
                              <WebMapDataType
                                type={"demand"}
                                data={dataTypes["demand"]}
                                setDataTypes={setDataTypes}
                              />
                            </div>
                            <div style={{ marginTop: "30px" }}></div>
                            <HorizontalSeparator />
                          </>
                        ) : (
                          <></>
                        )}
                        Score Range
                        <RangeSelectSlider
                          value={demandScoreRange}
                          setValue={setDemandScoreRange}
                        />
                        <HorizontalSeparator />
                        <Grid
                          container
                          xs={12}
                          sm={12}
                          md={12}
                          style={{
                            justifyContent: "space-between",
                          }}
                        >
                          <ToggleTitleWrapper main>No Data</ToggleTitleWrapper>
                          <ToggleSwitch
                            checked={isDemandNoDataChecked}
                            onChange={(e) => {
                              setIsDemandNoDataChecked(e.target.checked);
                              onChangeDemandNoDataToggle(e.target.checked);
                            }}
                          />
                        </Grid>
                      </Grid>
                    ) : null}

                    {demandType === DemandScoreTypes.AFFLUENCE ? (
                      <AffluenceFilterComponent />
                    ) : null}
              </Grid>
            )}
          </>
            ) : (
              <></>
            )}
          </>
        )}

        {capacityPerms?.is_enable === true && (
          <>
            {sectionsVisibilty.capacity ? (
              <>
                <SidePanelCard
                  title="Capacity"
                  icon={
                    isCapacityChecked
                      ? "/assets/capacity_active.png"
                      : "/assets/capacity_default.png"
                  }
                  nonShow={true}
                  clicked={isCapacityChecked}
                  onClick={capacitySidePanelOnClick}
                  isVisibleAsGreen={true}
                  visibilityClicked={isCapacityVisible}
                  onClickVisibility={onClickCapacityVisibility}
                />

                <CapacitySidePanel isPanelOpen={isCapacityChecked} />
              </>
            ) : (
              <></>
            )}
          </>
        )}

        {msPerms?.is_enable === true && (
          <>
            {sectionsVisibilty.markeShare ?
              <MarketSharePanel
                isMarketShareChecked={isMarketShareChecked}
                isMarketShareVisible={isMarketShareVisible}
                marketShareSidePanelOnClick={marketShareSidePanelOnClick}
                onClickMarketShareVisibility={onClickMarketShareVisibility}
                toggleTopMarketShare={toggleTopMarketShare}
                topMSPerm={topMSPerm}
                opMSPerm={opMSPerm}
                topMSAggrPerm = {topMSAggrPerm}
                opMSAggrPerm = {opMSAggrPerm}
                opMSChartView={opMSChartView}
              />
            : <></>}
          </>
        )}

        {ibsPerms?.is_enable === true && (
          <>
            {sectionsVisibilty.ibs ?
              <IBSPanel
                isIBSChecked={isIBSChecked}
                // setIsIBSChecked={setIsIBSChecked} // If needed for two-way binding
                isIBSVisible={isIBSVisible}
                // setIsIBSVisible={setIsIBSVisible} // If needed for two-way binding
                onBestIbsChange={setIsBestIBSChecked}
                ibsSidePanelOnClick={ibsSidePanelOnClick}
                onClickIBSVisibility={onClickIBSVisibility}
                ibsChartPerm={ibsChartPerm}
                opIbsPerm={opIbsPerm}
                bestIbsPerm={bestIbsPerm}
              />
            : <></>
            }
          </>
        )}

        {sitesPerms?.is_enable === true && (
          <>
            {sectionsVisibilty.site ? (
              <>
                <SidePanelCard
                  title="Sites"
                  icon={
                    isSitesChecked
                      ? "/assets/sites_white.png"
                      : "/assets/antenna.png"
                  }
                  nonShow={true}
                  clicked={isSitesChecked}
                  onClick={siteSidePanelOnClick}
                  isVisibleAsGreen={isEyeIconVisibleAsGreenForSites}
                  visibilityClicked={isSitesVisible}
                  onClickVisibility={onClickSitesVisibility}
                />
                {edotcoSitePerm?.is_enable === true && (
                  <>
                    <Fragment>
                      <EdotCoSitesCards
                        isSitesChecked={isSitesChecked}
                        isECoSitesChecked={isECoSitesChecked}
                        handleECoSitesToggle={handleECoSitesToggle}
                        ecoGAPerm={ecoGAPerm}
                        ecoCTPerm={ecoCTPerm}
                        ecoDTPerm={ecoDTPerm}
                        exitingTenantsPerm={exitingTenantsPerm}
                        proposedTenantsPerm={proposedTenantsPerm}
                        edotcoSitePerm={edotcoSitePerm?.is_enable}
                      />
                    </Fragment>
                  </>
                )}
                {isSitesChecked && opSitePerm?.is_enable && (
                  <HorizontalSeparator />
                )}
                {opSitePerm?.is_enable === true && (
                  <>
                    <OperatorSitesCards
                      isSitesChecked={isSitesChecked}
                      isOpSitesChecked={isOpSitesChecked}
                      handleOpSitesToggle={handleOpSitesToggle}
                      opGAPerm={opGAPerm}
                      edotcoSitePerm={edotcoSitePerm}
                    />
                  </>
                )}
              </>
            ) : (
              <></>
            )}
          </>
        )}

        <>
          {selectedCountryPrems?.find((p) => p.name === "Tools")?.is_enable ===
          true ? (
            <PanelTools
              show={show}
              permissions={
                selectedCountryPrems?.find((p) => p.name === "Tools")
                  ?.sub_permissions
              }
            />
          ) : null}
        </>

        {mapPlacement !== "right" && uploadFilesPerms?.is_enable && (
          <>
            <SidePanelCard
              title="Uploaded Files"
              icon={
                isUploadFilesVisible
                  ? "/assets/fileUploadActive.png"
                  : "/assets/fileUploadInactive.png"
              }
              nonShow={false}
              clicked={isUploadFilesVisible}
              onClick={uploadSidePanelOnClick}
              isVisibleAsGreen={true}
            />

            <UploadFilesSidePanel isPanelOpen={isUploadFilesVisible} />
          </>
        )}
      </div>
    </Context.Provider>
  );
}
